import { snapshotTiming } from "./timelineSnapshotFields";

/**
 * One timeline row, keyed by the server's `activity_id`.
 *
 * Live SSE events and `work_status` snapshots describe the same work, so they
 * merge into one list by id rather than living in two arrays that the renderer
 * has to choose between. Statuses use the checkpoint's vocabulary throughout
 * (`running | succeeded | failed | superseded`) so a merged row means the same
 * thing whichever side produced it.
 */

export function upsertRow(rows, row) {
  const index = rows.findIndex((item) => item.id === row.id);
  if (index === -1) return [...rows, row];
  const next = rows.slice();
  next[index] = { ...next[index], ...row };
  return next;
}

export function snapshotRow(raw) {
  return {
    id: raw.id,
    taskId: raw.task_id ?? null,
    tool: raw.tool_slug ?? null,
    label: raw.label,
    status: raw.status,
    attribution: raw.attribution ?? null,
    startedSeq: raw.started_seq ?? null,
    originRunId: raw.origin_run_id ?? null,
    ...snapshotTiming(raw),
  };
}

/**
 * Snapshot wins field-by-field over what the browser holds — it is the
 * authoritative attribution, and re-parenting an optimistically inserted row is
 * a move rather than a duplicate because both sides key on the same id.
 *
 * Rows are dropped only when the server names them in `activity_evicted_ids`.
 * A row the checkpoint never accepted cannot appear there, so a live fallback
 * row survives however far the ring buffer has moved on.
 */
export function mergeSnapshotRows(rows, snapshot) {
  const evicted = new Set(snapshot?.activity_evicted_ids ?? []);
  const kept = evicted.size ? rows.filter((row) => !evicted.has(row.id)) : rows;
  return (snapshot?.activity ?? []).reduce(
    (acc, raw) => upsertRow(acc, snapshotRow(raw)),
    kept,
  );
}
